'use client';

import { useState } from 'react';
import { IconChevronDown } from '@repo/svgs';
import { cn } from '@/lib/cn';
import { MaskedText } from '@/components/ui/MaskedText';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { useBuilding } from '@/app/building/context/BuildingProvider';

export function BuildingReviewSummary() {
  const {
    building: { reviewSummary },
    isMasked,
  } = useBuilding();

  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (!reviewSummary) {
    return null;
  }

  // Show toggle if summary is likely to exceed 5 lines (roughly 400+ characters)
  const shouldShowToggle = !isMasked && reviewSummary.length > 400;

  return (
    <div>
      <SectionHeader
        title="Review summary"
        subtitleText="Summarized from anonymous rental reviews on Openigloo."
      />

      <div className={cn('flex flex-col items-start py-0 w-full', 'px-2', 'md:px-4')}>
        {isMasked ? (
          // Full summary stays in the DOM for SEO; users only see the first line + masked bars.
          <div className="text-body-2 text-primary w-full select-none">
            <p className="overflow-hidden whitespace-nowrap">{reviewSummary}</p>
            <div className="mt-1 space-y-1" aria-hidden="true">
              <MaskedText className="w-full" />
              <MaskedText className="w-[92%]" />
              <MaskedText className="w-full" />
              <MaskedText className="w-[97%]" />
            </div>
          </div>
        ) : (
          <div
            className={cn(
              'text-body-2 text-primary w-full',
              !isExpanded && shouldShowToggle && 'line-clamp-5',
            )}
          >
            {reviewSummary}
          </div>
        )}

        {shouldShowToggle && (
          <button
            onClick={toggleExpanded}
            className="flex gap-1 items-center justify-center px-0 py-2 mt-2"
          >
            <span className="text-body-2-high text-accent text-center whitespace-nowrap">
              {isExpanded ? 'View less' : 'View more'}
            </span>
            <div className="w-3 h-3 flex-shrink-0">
              <IconChevronDown
                className={cn(
                  'w-full h-full icon-accent transition-transform duration-200',
                  isExpanded && 'rotate-180',
                )}
              />
            </div>
          </button>
        )}
      </div>
    </div>
  );
}
